<template>
  <div class="panel-section p-bottom-8">
    <div
      class="is-flex is-align-items-center is-justify-content-space-between"
      :class="{
        'panel-item panel-item--editable': $can('manage', 'Request')
      }"
    >
      <p class="has-text-weight-semibold">Point of contact</p>
      <NrTooltip
        label="Change point of contact"
        v-if="$can('manage', 'Request')"
      >
        <NrIconButton
          variant="minimal-neutral"
          :icon="{ name: 'pencil-alt', type: 'regular' }"
          aria-label="Change point of contact"
          @click="setCurrentAction('edit-poc')"
        />
      </NrTooltip>
    </div>

    <div class="m-left-8 m-bottom-12">
      <NrTooltip
        v-if="request.poc.undeliverable_email"
        label="Mail to this address has been returned as undeliverable"
      >
        <p class="has-text-red t--strikethrough">
          {{ request.poc.email_or_name }}
        </p>
      </NrTooltip>
      <p v-if="!request.poc.undeliverable_email">
        <NrTooltip
          v-if="staffDepartmentsWithPocLabel() != ''"
          :label="staffDepartmentsWithPocLabel()"
        >
          {{ request.poc.email_or_name }}
        </NrTooltip>
        <span v-else>
          {{ request.poc.email_or_name }}
        </span>
      </p>
    </div>

    <NrModal
      primary-button-text="Change contact"
      secondary-button-text="Cancel"
      title="Change Point of Contact"
      :has-secondary-button="true"
      :show="currentAction === 'edit-poc'"
      @on-close-click="setCurrentAction(null)"
      @on-primary-click="submitForm"
      @on-secondary-click="setCurrentAction(null)"
    >
      <div class="field">
        <NrSelect
          v-model="selectedPoc"
          :filterable="false"
          placeholder="Search and select staff"
          :options="filteredStaff"
          :loading="dataFetchState.staff.fetching"
          :hasMore="!dataFetchState.staff.end"
          @load-more="handleStaffScroll"
          @search="filterStaff"
          @open="filterStaff"
          append-to-body
        >
          <template v-slot:label>
            <span>Search staff</span>
          </template>
        </NrSelect>
      </div>
      <div v-if="request.poc.has_tasks" class="field">
        <NrFormInput
          type="checkbox"
          label="Reassign tasks"
          name="reassign_tasks"
          v-model="reassignTasks"
        />
        <p class="help">
          Reassign the current point of contact's tasks to the new point of
          contact? Uncheck this to add the current point of contact as support
          staff and keep the current task assignment.
        </p>
      </div>
    </NrModal>
  </div>
</template>

<script lang="ts">
import { mapState, mapActions } from 'vuex'
import throttle from 'lodash/throttle'
import NrIconButton from '@nextrequestco/components/src/components/NrIconButton'
import NrModal from '@nextrequestco/components/src/components/NrModal'
import NrTooltip from '@nextrequestco/components/src/components/NrTooltip'
import NrSelect from '@nextrequestco/components/src/components/NrSelect'
import NrFormInput from '@nextrequestco/components/src/components/NrFormInput'
import NrSentry from '../../../utils/nr_sentry'

interface Option {
  value: number
  label: string // eslint-disable-line camelcase
}

interface Staff {
  departments: Array<object | null>
  email: string
  id: number
  name_or_email: string // eslint-disable-line camelcase
  name_with_email: string // eslint-disable-line camelcase
  undeliverable_email: boolean // eslint-disable-line camelcase
}

export default {
  components: {
    NrIconButton,
    NrTooltip,
    NrModal,
    NrSelect,
    NrFormInput
  },
  data() {
    return {
      selectedPoc: {},
      reassignTasks: false
    }
  },
  mounted() {
    this.selectedPoc = {
      value: this.request.poc.id,
      label: this.request.poc.name_with_email
    }
  },
  computed: {
    ...mapState('request_show', [
      'request',
      'requestTasks',
      'requestStaff',
      'currentAction',
      'dataOptions',
      'dataFetchState'
    ]),
    filteredStaff() {
      return this.dataOptions.staff.map((staff: Staff) => ({
        label: staff.name_with_email,
        value: staff.id
      }))
    }
  },
  methods: {
    ...mapActions('request_show', [
      'fetchRequestStaff',
      'setCurrentAction',
      'updateRequest',
      'fetchRequestTasks',
      'fetchDataOptions'
    ]),
    staffDepartmentsWithPocLabel(): string {
      const departmentsList = []
      const staffPoc = this.requestStaff.find(
        (staff) => staff.id === this.request.poc.id
      )
      if (staffPoc === undefined) {
        this.request.departments.forEach((department) => {
          if (department.poc_id === this.request.poc.id) {
            departmentsList.unshift(department.name + ' (Point of Contact)')
          }
        })
      } else {
        this.request.departments.forEach((department) => {
          if (staffPoc?.departments.includes(department.name)) {
            if (department.poc_id === staffPoc.id) {
              departmentsList.unshift(department.name + ' (Point of Contact)')
            } else {
              departmentsList.push(department.name)
            }
          }
        })
      }
      return departmentsList.join(', ')
    },
    filterStaff: throttle(function (searchText = '') {
      this.fetchDataOptions({ searchText, dataType: 'staff' })
    }, 400),
    handleStaffScroll: throttle(function () {
      this.fetchDataOptions({
        searchText: this.dataFetchState.staff.search_text,
        dataType: 'staff'
      })
    }, 400),
    setPoc(poc: Option): void {
      this.selectedPoc = poc
    },
    submitForm(): void {
      if (!this.selectedPoc) {
        this.$toasted.error('Please select a point of contact')
        return
      }
      const params = {
        request_id: this.request.pretty_id,
        poc_id: this.selectedPoc.value,
        reassign_tasks: this.reassignTasks
      }
      this.updateRequest(params)
        .then(() => {
          this.$toasted.success('Successfully updated the point of contact')
          this.setCurrentAction(null)
          this.reassignTasks = false
          this.fetchRequestStaff({ request_id: this.request.pretty_id }).catch(
            () => {
              this.$toasted.error(
                'There was an error retrieving the request staff'
              )
            }
          )
          this.fetchRequestTasks({ pretty_id: this.request.pretty_id })
        })
        .catch((e) => {
          this.$toasted.error('There was an error updating the request')
          NrSentry.captureError(e)
        })
    }
  }
}
</script>
